(() => {
  'use strict';

  if (window.tbvCollectionSidebarStickyV3Loaded) return;
  window.tbvCollectionSidebarStickyV3Loaded = true;

  const desktopQuery = window.matchMedia('(min-width: 1024px)');
  let column;
  let sidebar;
  let grid;
  let columnTop = 0;
  let columnLeft = 0;
  let sidebarLeft = 0;
  let sidebarOffsetLeft = 0;
  let sidebarWidth = 0;
  let gridBottom = 0;
  let fixedHeight = 0;
  let currentState = '';
  let frameRequested = false;
  let resizeObserver;
  let mutationObserver;
  let mutationFrame;

  const readPixelVariable = (name, fallback) => {
    const value = parseFloat(getComputedStyle(document.documentElement).getPropertyValue(name));
    return Number.isFinite(value) ? value : fallback;
  };

  const stickyTop = () => (
    readPixelVariable('--tbv-topbar-height', 31)
    + readPixelVariable('--tbv-header-height', 58)
    + 18
  );

  const clearSidebarStyles = () => {
    if (!sidebar) return;
    [
      'position',
      'top',
      'left',
      'right',
      'bottom',
      'width',
      'max-height',
      'overflow-x',
      'overflow-y',
      'overscroll-behavior',
      'transform',
      'will-change',
      'z-index',
    ].forEach((property) => sidebar.style.removeProperty(property));
    currentState = '';
  };

  const setSharedStickyStyles = () => {
    const availableHeight = Math.max(240, window.innerHeight - stickyTop() - 18);
    sidebar.style.setProperty('max-height', `${availableHeight}px`, 'important');
    sidebar.style.setProperty('overflow-x', 'hidden', 'important');
    sidebar.style.setProperty('overflow-y', 'auto', 'important');
    sidebar.style.setProperty('overscroll-behavior', 'contain');
    sidebar.style.setProperty('transform', 'none', 'important');
    sidebar.style.setProperty('will-change', 'auto');
    sidebar.style.setProperty('z-index', '20');
  };

  const applyState = (nextState) => {
    if (nextState === currentState) return;
    currentState = nextState;

    if (nextState === 'top') {
      clearSidebarStyles();
      currentState = 'top';
      sidebar.style.setProperty('position', 'relative', 'important');
      return;
    }

    setSharedStickyStyles();
    sidebar.style.setProperty('width', `${sidebarWidth}px`, 'important');

    if (nextState === 'fixed') {
      sidebar.style.setProperty('position', 'fixed', 'important');
      sidebar.style.setProperty('top', `${stickyTop()}px`, 'important');
      sidebar.style.setProperty('left', `${sidebarLeft}px`, 'important');
      sidebar.style.removeProperty('bottom');
      return;
    }

    sidebar.style.setProperty('position', 'absolute', 'important');
    sidebar.style.setProperty('top', `${Math.max(0, gridBottom - columnTop - fixedHeight)}px`, 'important');
    sidebar.style.setProperty('left', `${sidebarOffsetLeft}px`, 'important');
    sidebar.style.removeProperty('bottom');
  };

  const update = () => {
    frameRequested = false;
    if (!desktopQuery.matches || !column || !sidebar || !grid) {
      clearSidebarStyles();
      return;
    }

    const top = stickyTop();
    const startAt = columnTop - top;
    const stopAt = gridBottom - top - fixedHeight;

    if (window.scrollY <= startAt || stopAt <= startAt) {
      applyState('top');
    } else if (window.scrollY >= stopAt) {
      applyState('bottom');
    } else {
      applyState('fixed');
    }
  };

  const requestUpdate = () => {
    if (frameRequested) return;
    frameRequested = true;
    window.requestAnimationFrame(update);
  };

  const measure = () => {
    const previousSidebar = sidebar;
    column = document.querySelector('.collection-template-wrapper > .collection-main-filters');
    grid = document.querySelector('.collection-template-wrapper > .collection-main-grid');
    sidebar = column?.querySelector('[data-armada-selector="filter-sidebar-container"]');

    if (previousSidebar && previousSidebar !== sidebar) {
      clearSidebarStyles();
    }

    if (!desktopQuery.matches || !column || !sidebar || !grid) {
      clearSidebarStyles();
      return;
    }

    clearSidebarStyles();
    column.style.setProperty('position', 'relative', 'important');
    column.style.setProperty('align-self', 'stretch', 'important');
    column.style.setProperty('overflow', 'visible', 'important');

    const scrollPosition = window.scrollY;
    const columnRect = column.getBoundingClientRect();
    const gridRect = grid.getBoundingClientRect();
    const sidebarRect = sidebar.getBoundingClientRect();
    const naturalHeight = sidebarRect.height;
    const availableHeight = Math.max(240, window.innerHeight - stickyTop() - 18);

    columnTop = columnRect.top + scrollPosition;
    columnLeft = columnRect.left;
    sidebarLeft = sidebarRect.left;
    sidebarOffsetLeft = sidebarRect.left - columnRect.left;
    sidebarWidth = sidebarRect.width;
    gridBottom = gridRect.bottom + scrollPosition;
    fixedHeight = Math.min(naturalHeight, availableHeight);
    update();
  };

  const observeLayout = () => {
    resizeObserver?.disconnect();
    mutationObserver?.disconnect();

    if ('ResizeObserver' in window && grid) {
      resizeObserver = new ResizeObserver(() => {
        window.cancelAnimationFrame(mutationFrame);
        mutationFrame = window.requestAnimationFrame(measure);
      });
      resizeObserver.observe(grid);
      resizeObserver.observe(column);
    }

    const wrapper = document.querySelector('.collection-template-wrapper');
    if (wrapper) {
      mutationObserver = new MutationObserver(() => {
        window.cancelAnimationFrame(mutationFrame);
        mutationFrame = window.requestAnimationFrame(() => {
          measure();
          observeLayout();
        });
      });
      mutationObserver.observe(wrapper, { childList: true, subtree: true });
    }
  };

  const initialize = () => {
    measure();
    observeLayout();
    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', measure, { passive: true });
    window.addEventListener('load', measure, { once: true });
    desktopQuery.addEventListener?.('change', measure);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
